import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Layers,
  Plus,
  Trash2,
  Loader2,
  Save,
  Droplet,
  Wrench,
  Shield,
  Hammer,
  Zap,
  Thermometer,
  Droplets,
  Wind,
  Sparkles,
  FlaskConical,
  ChevronDown,
  ChevronUp,
  Tag,
  Users,
  UserPlus,
  X,
  Check,
  Search,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const WORK_CATEGORIES = [
  { id: "reverse_osmosis", name: "Reverse Osmosis", icon: Droplet, color: "bg-blue-100 text-blue-700" },
  { id: "deionization", name: "Deionization", icon: FlaskConical, color: "bg-purple-100 text-purple-700" },
  { id: "plumbing", name: "Plumbing", icon: Wrench, color: "bg-amber-100 text-amber-700" },
  { id: "containment", name: "Containment", icon: Shield, color: "bg-emerald-100 text-emerald-700" },
  { id: "general_contracting", name: "General Contracting", icon: Hammer, color: "bg-slate-100 text-slate-700" },
  { id: "electrical", name: "Electrical", icon: Zap, color: "bg-yellow-100 text-yellow-700" },
  { id: "hvac", name: "HVAC", icon: Thermometer, color: "bg-red-100 text-red-700" },
  { id: "water_treatment", name: "Water Treatment", icon: Droplets, color: "bg-cyan-100 text-cyan-700" },
  { id: "medical_gas", name: "Medical Gas", icon: Wind, color: "bg-indigo-100 text-indigo-700" },
  { id: "sterilization", name: "Sterilization", icon: Sparkles, color: "bg-pink-100 text-pink-700" },
];

export default function WorkScopeManager({ entityType, entityId, entityName, initialWorkScope = [], initialTaggedUsers = [], initialPendingInvites = [], onUpdate }) {
  const { token } = useAuth();
  const [workScope, setWorkScope] = useState(initialWorkScope);
  const [taggedUsers, setTaggedUsers] = useState(initialTaggedUsers);
  const [pendingInvites, setPendingInvites] = useState(initialPendingInvites);
  const [taggableUsers, setTaggableUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddScope, setShowAddScope] = useState(false);
  const [showTagUsers, setShowTagUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  
  const [newScope, setNewScope] = useState({
    category_id: "",
    description: ""
  });

  useEffect(() => {
    fetchTaggableUsers();
  }, []);

  useEffect(() => {
    setWorkScope(initialWorkScope);
    setTaggedUsers(initialTaggedUsers);
    setPendingInvites(initialPendingInvites);
  }, [initialWorkScope, initialTaggedUsers, initialPendingInvites]);

  const fetchTaggableUsers = async () => {
    try {
      const res = await fetch(`${API}/users/taggable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTaggableUsers(data.users || []);
      }
    } catch (err) {
      console.error("Failed to fetch taggable users", err);
    }
  };

  const handleAddScope = () => {
    if (!newScope.category_id) {
      toast.error("Please select a work category");
      return;
    }
    if (!newScope.description.trim()) {
      toast.error("Please add a description");
      return;
    }

    setWorkScope([...workScope, { ...newScope }]);
    setNewScope({ category_id: "", description: "" });
    setShowAddScope(false);
    setHasChanges(true);
  };

  const handleRemoveScope = (index) => {
    const updated = [...workScope];
    updated.splice(index, 1);
    setWorkScope(updated);
    setHasChanges(true);
  };

  const handleUpdateDescription = (index, description) => {
    const updated = [...workScope];
    updated[index].description = description;
    setWorkScope(updated);
    setHasChanges(true);
  };

  const handleTagUser = (user) => {
    if (taggedUsers.some(u => u.id === user.id)) {
      // Remove user
      setTaggedUsers(taggedUsers.filter(u => u.id !== user.id));
    } else {
      // Add user
      setTaggedUsers([...taggedUsers, user]);
    }
    setHasChanges(true);
  };

  const handleInviteByEmail = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Check if already tagged or pending
    if (taggedUsers.some(u => u.email?.toLowerCase() === inviteEmail.toLowerCase())) {
      toast.error("This user is already tagged");
      return;
    }
    if (pendingInvites.some(e => e.toLowerCase() === inviteEmail.toLowerCase())) {
      toast.error("An invitation is already pending for this email");
      return;
    }
    
    setSendingInvite(true);
    try {
      const endpoint = entityType === "project" 
        ? `${API}/admin/projects/${entityId}/tag-by-email`
        : `${API}/admin/maintenance/${entityId}/tag-by-email`;
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.type === "registered_user") {
          toast.success(`${inviteEmail} has been tagged and notified`);
          // Refresh to get updated tagged users
          if (onUpdate) onUpdate();
        } else if (data.type === "invite_sent" || data.type === "invite_resent") {
          toast.success(`Invitation sent to ${inviteEmail}`);
          setPendingInvites([...pendingInvites, inviteEmail]);
        } else if (data.status === "already_tagged") {
          toast.info(data.message);
        }
        setInviteEmail("");
      } else {
        toast.error(data.detail || "Failed to invite user");
      }
    } catch (err) {
      toast.error("Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = entityType === "project" 
        ? `${API}/admin/projects/${entityId}/work-scope`
        : `${API}/admin/maintenance/${entityId}/work-scope`;

      const res = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          work_scope: workScope,
          tagged_users: taggedUsers.map(u => u.id)
        })
      });

      if (res.ok) {
        toast.success("Work scope and tags updated");
        setHasChanges(false);
        if (onUpdate) onUpdate();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (err) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const getCategoryById = (id) => WORK_CATEGORIES.find(c => c.id === id);

  const filteredUsers = taggableUsers.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Work Scope Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Work Scope</h3>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {workScope.length}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowAddScope(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Category
          </Button>
        </div>

        {/* Work Scope Items */}
        {workScope.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <Layers className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>No work scope defined</p>
            <p className="text-xs mt-1">Click "Add Category" to define work</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workScope.map((item, index) => {
              const category = getCategoryById(item.category_id);
              const IconComponent = category?.icon || Layers;
              
              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category?.color || 'bg-slate-100 text-slate-700'}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900">
                          {category?.name || item.category_id}
                        </h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleRemoveScope(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleUpdateDescription(index, e.target.value)}
                        placeholder="Describe the work for this category..."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tagged Users Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-slate-900">Collaborators</h3>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {taggedUsers.length}
            </span>
            {pendingInvites.length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {pendingInvites.length} pending
              </span>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowTagUsers(true)}>
            <UserPlus className="w-4 h-4 mr-1" />
            Add Staff
          </Button>
        </div>

        {/* Active Tagged Users */}
        {taggedUsers.length === 0 && pendingInvites.length === 0 ? (
          <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No collaborators added</p>
            <p className="text-xs mt-1">Add staff by name, email, or Telegram handle</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Active Users */}
            {taggedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {taggedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full"
                  >
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" title="Active" />
                    <span className="text-sm text-emerald-700">{user.name}</span>
                    <span className="text-xs text-emerald-500 capitalize">({user.type})</span>
                    <button
                      onClick={() => handleTagUser(user)}
                      className="text-emerald-400 hover:text-emerald-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {pendingInvites.map((email, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full"
                  >
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" title="Pending Registration" />
                    <span className="text-sm text-amber-700">{email}</span>
                    <span className="text-xs text-amber-500">(pending)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end pt-4 border-t border-slate-200">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      )}

      {/* Add Scope Modal */}
      <Dialog open={showAddScope} onOpenChange={setShowAddScope}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Work Category</DialogTitle>
            <DialogDescription>
              Select a category and describe the work involved
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select
                value={newScope.category_id}
                onValueChange={(value) => setNewScope({ ...newScope, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select work category" />
                </SelectTrigger>
                <SelectContent>
                  {WORK_CATEGORIES.map((cat) => {
                    const IconComp = cat.icon;
                    return (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <IconComp className="w-4 h-4" />
                          {cat.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description of Work</Label>
              <Textarea
                value={newScope.description}
                onChange={(e) => setNewScope({ ...newScope, description: e.target.value })}
                placeholder="Describe the specific work for this category..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddScope(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddScope}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Users Modal */}
      <Dialog open={showTagUsers} onOpenChange={setShowTagUsers}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Collaborators</DialogTitle>
            <DialogDescription>
              Add staff to this ticket. They&apos;ll receive all updates via email and Telegram.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Invite by Email Section */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-slate-900">Invite by Email</h4>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Add anyone by email. If they&apos;re not registered, they&apos;ll receive an invitation to join.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter email address..."
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInviteByEmail()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleInviteByEmail} 
                  disabled={sendingInvite}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingInvite ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or select from registered users</span>
              </div>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredUsers.map((user) => {
                const isTagged = taggedUsers.some(u => u.id === user.id);
                return (
                  <button
                    key={user.id}
                    onClick={() => handleTagUser(user)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                      isTagged 
                        ? 'bg-emerald-50 border border-emerald-200' 
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-xs text-slate-500">
                        {user.email} • <span className="capitalize">{user.type}</span>
                        {user.health_system && ` • ${user.health_system}`}
                      </p>
                    </div>
                    {isTagged ? (
                      <div className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                    )}
                  </button>
                );
              })}

              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No users found</p>
                  <p className="text-xs mt-1">Try inviting by email instead</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowTagUsers(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
